import React, { forwardRef, useEffect, useState, useCallback } from "react";
import { ColumnTypeIcon } from "../utils/columnUtils";
import { renderDataCellValue } from "../utils/renderUtils";
import { Z_INDEX } from "../utils/tooltips";
import { useVisibleColumns } from "../hooks/useVisibleColumns";

// Column Edit Popover Component
const ColumnEditPopover = ({
  editColumnName,
  setEditColumnName,
  editColumnType,
  setEditColumnType,
  columnTypeOptions,
  onSave,
  onCancel,
  isDarkMode,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div
      className={`absolute left-0 top-full mt-2 rounded-lg shadow-2xl border p-3 min-w-[220px] animate-in fade-in slide-in-from-top-1 duration-150 ${
        isDarkMode
          ? "bg-[#1a1a1c] border-white/[0.1] shadow-black/50"
          : "bg-white border-gray-200 shadow-xl"
      }`}
      style={{ zIndex: Z_INDEX.DROPDOWN }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-3">
        <div>
          <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1.5 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}>
            Column Name
          </label>
          <input
            type="text"
            value={editColumnName}
            onChange={(e) => setEditColumnName(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 transition-colors ${
              isDarkMode
                ? "bg-white/[0.06] border border-white/[0.1] text-white focus:ring-blue-500/40 focus:border-blue-500/40 placeholder-gray-500"
                : "bg-gray-50 border border-gray-200 text-gray-800 focus:ring-blue-500/40 focus:border-blue-400"
            }`}
            autoFocus
            placeholder="Enter column name"
          />
        </div>
        <div>
          <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1.5 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}>
            Column Type
          </label>
          <div className="relative">
            <select
              value={editColumnType}
              onChange={(e) => setEditColumnType(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`appearance-none w-full px-3 pr-8 py-2 rounded-md text-sm focus:outline-none focus:ring-2 transition-colors cursor-pointer ${
                isDarkMode
                  ? "bg-white/[0.06] border border-white/[0.1] text-white focus:ring-blue-500/40"
                  : "bg-gray-50 border border-gray-200 text-gray-800 focus:ring-blue-500/40"
              }`}
            >
              {columnTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.emoji} {option.label}
                </option>
              ))}
            </select>
            <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-200 hover:bg-white/[0.06]"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
              isDarkMode
                ? "bg-blue-600 text-white hover:bg-blue-500"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete feedback toast
const DeleteFeedback = ({ message, isDarkMode, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-lg shadow-xl flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200 ${
        isDarkMode
          ? "bg-[#1a1a1c] border border-white/[0.1] text-gray-200"
          : "bg-white border border-gray-200 text-gray-700 shadow-lg"
      }`}
      style={{ zIndex: Z_INDEX.TOAST }}
    >
      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <span className="text-sm font-medium">{message}</span>
    </div>
  );
};

const TableComponent = forwardRef(({
  displayData,
  processedData,
  isDarkMode,
  isPaginating = false,
  previousDataLength,
  newRowsCount,
  columnWidths,
  resizingColumn,
  handleResizeMouseDown,
  sortConfig,
  requestSort,
  draggedColumn,
  handleColumnDragStart,
  handleColumnDragOver,
  handleColumnDragLeave,
  handleColumnDrop,
  editingColumnId,
  editColumnName,
  editColumnType,
  setEditingColumnId,
  setEditColumnName,
  setEditColumnType,
  handleColumnEditStart,
  handleColumnEditSave,
  toggleFilter,
  filters,
  handleColumnMerge,
  deletingColumn,
  handleColumnDelete,
  columns,
  editingCell,
  editValue,
  setEditValue,
  handleCellEdit,
  handleCellEditCancel,
  handleCellEditSave,
  columnTypeOptions,
  automationActive,
  automationRunning,
  selectedRowId,
  onRowSelect,
  onOpenGallery,
  deletedColumnName,
  onDeleteFeedbackComplete,
  onRowDelete,
  deletingRowId,
  rowDeleteFeedback,
  onRowDeleteFeedbackComplete,
}, ref) => {
  // Column virtualization — only renders visible columns when there are many.
  // Header cells report their rendered widths through measureColumn, so the
  // spacers match the columns they stand in for.
  const { visibleColumns, leftPadding, rightPadding, scrollInfo, measureColumn } = useVisibleColumns(
    processedData.displayColumns,
    columnWidths,
    ref,
  );
  // The left spacer carries the right border of the last column it replaces.
  // Borders collapse, so without it the first rendered column would lose half
  // of its left border, and the columns after it would creep left by half a
  // pixel whenever the range moves.
  const leftSpacerBorder = `border-r ${isDarkMode ? "border-white/[0.04]" : "border-gray-100"}`;

  // When image columns exist but may be scrolled off-screen, lock row height
  // so rows don't jump between tall (image) and short (text-only) heights.
  const hasImageColumns = processedData.displayColumns.some(
    (c) => c.type === "image" || c.type === "image_previews" || c.type === "image_group_toggle"
  );

  // Single delegated tooltip state (replaces per-cell CellTooltip components)
  const [tooltip, setTooltip] = useState(null);

  const handleCellMouseOver = useCallback((e) => {
    // Walk up from target to find nearest [data-tooltip] element
    let el = e.target;
    while (el && el !== e.currentTarget) {
      if (el.hasAttribute("data-tooltip")) {
        // Only show tooltip if content is truncated
        if (el.scrollWidth <= el.clientWidth) return;
        const value = el.getAttribute("data-tooltip");
        const error = el.getAttribute("data-tooltip-error") || null;
        if (!value && !error) return;

        const rect = el.getBoundingClientRect();
        const spaceAbove = rect.top;
        const showAbove = spaceAbove > 80;
        let left = rect.left;
        const tooltipMaxWidth = 320;
        if (left + tooltipMaxWidth > window.innerWidth - 12) {
          left = window.innerWidth - tooltipMaxWidth - 12;
        }
        if (left < 12) left = 12;

        setTooltip({
          value,
          error,
          top: showAbove ? rect.top : rect.bottom,
          left,
          above: showAbove,
        });
        return;
      }
      el = el.parentElement;
    }
  }, []);

  const handleCellMouseOut = useCallback((e) => {
    let el = e.target;
    while (el && el !== e.currentTarget) {
      if (el.hasAttribute("data-tooltip")) {
        setTooltip(null);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  return (
    <div
      className="flex flex-col flex-1 transition-opacity duration-300 overflow-hidden"
      style={{
        position: "relative",
        height: "100%",
      }}
    >
      {/* Loading overlay for pagination */}
      {isPaginating && (
        <div className={`absolute inset-0 z-40 flex items-center justify-center backdrop-blur-[2px] ${isDarkMode ? "bg-black/50" : "bg-white/50"}`}>
          <div className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? "border-white/40" : "border-gray-400"}`} />
        </div>
      )}
      <div
        ref={ref}
        className="overflow-auto custom-scrollbar flex-1"
        style={{
          maxWidth: "100%",
        }}
      >
          <table className={`min-w-full text-xs transition-colors duration-300 relative z-10 ${isDarkMode ? "bg-[#111113]" : "bg-white"}`}>
            <thead>
              <tr className={`sticky top-0 z-[100] transition-colors duration-300 ${
                isDarkMode
                  ? "bg-[#0a0a0b] border-b border-white/[0.06]"
                  : "bg-[#fafafa] border-b border-gray-200/80"
              }`}>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 border-r ${
                    isDarkMode ? "border-white/[0.04] text-gray-400" : "border-gray-100 text-gray-500"
                  } ${sortConfig.key === "id" ? (isDarkMode ? "bg-white/[0.02]" : "bg-gray-100/50") : ""}`}
                  style={{ width: "1%", minWidth: "62px", whiteSpace: "nowrap" }}
                  onClick={() => requestSort("id")}
                >
                  <div className="flex items-center cursor-pointer group">
                    <span>#</span>
                    {sortConfig.key === "id" ? (
                      <span className="ml-1 text-[10px]">{sortConfig.direction === "ascending" ? "↑" : "↓"}</span>
                    ) : (
                      <span className="ml-1 text-[10px] opacity-0 group-hover:opacity-40">↕</span>
                    )}
                  </div>
                </th>
                {leftPadding > 0 && <th className={leftSpacerBorder} style={{ width: leftPadding, minWidth: leftPadding, padding: 0 }} />}
                {visibleColumns.map((column) => (
                  <th
                    key={column.id}
                    ref={measureColumn(column.id)}
                    className={`relative px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 border-r ${
                      isDarkMode ? "border-white/[0.04] text-gray-400" : "border-gray-100 text-gray-500"
                    } ${sortConfig.key === column.id ? (isDarkMode ? "bg-white/[0.02]" : "bg-gray-100/50") : ""}`}
                    style={{
                      width: (column.id === 'image_previews' || column.type === 'image_group_toggle')
                        ? '1%'
                        : (columnWidths[column.id] || 150),
                      minWidth: 80,
                      maxWidth: (column.id === 'image_previews' || column.type === 'image_group_toggle')
                        ? undefined
                        : (columnWidths[column.id] || "auto"),
                      position: "relative",
                      whiteSpace: (column.id === 'image_previews' || column.type === 'image_group_toggle') ? 'nowrap' : undefined,
                    }}
                    draggable={column.id !== "image_previews" && column.type !== "image_group_toggle"}
                    onDragStart={() => handleColumnDragStart(column.id)}
                    onDragOver={(e) => handleColumnDragOver(e, column.id)}
                    onDragLeave={handleColumnDragLeave}
                    onDrop={(e) => handleColumnDrop(e, column.id)}
                  >
                    {/* Normal column header - always visible, popover shows separately */}
                    <div className="flex items-center justify-between group/header">
                      <div className="flex items-center flex-1 min-w-0">
                        {/* Image column group toggle - entire header is clickable */}
                        {(column.type === "image_previews" || column.type === "image_group_toggle") ? (
                          <div className="relative group/toggle flex items-center flex-1 min-w-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFilter("imageColumnsExpanded");
                              }}
                              className={`flex items-center min-w-0 rounded transition-colors ${
                                isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-100"
                              }`}
                            >
                              <span className="mr-1.5 flex-shrink-0">
                                <svg
                                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                    column.type === "image_group_toggle" ? "rotate-90" : ""
                                  } ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </span>
                              <span className={`truncate font-medium uppercase transition-colors ${
                                isDarkMode ? "text-gray-300 group-hover/toggle:text-white" : "text-gray-600 group-hover/toggle:text-gray-900"
                              }`}>
                                {column.name || column.id}
                              </span>
                            </button>
                            {/* Custom instant tooltip */}
                            <div
                              className={`absolute left-0 top-full mt-1 px-2 py-1 text-[10px] font-normal normal-case tracking-normal rounded shadow-lg whitespace-nowrap opacity-0 group-hover/toggle:opacity-100 transition-opacity duration-150 pointer-events-none ${
                                isDarkMode ? "bg-zinc-800 text-gray-200 border border-zinc-700" : "bg-gray-800 text-white"
                              }`}
                              style={{ zIndex: Z_INDEX.TOOLTIP }}
                            >
                              {column.type === "image_previews"
                                ? `Click to expand all ${column.imageColumnCount} image URL columns`
                                : `Click to collapse into preview`}
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="mr-1.5 flex-shrink-0 transition-transform duration-150 group-hover/header:scale-110">
                              <ColumnTypeIcon type={column.type} isDarkMode={isDarkMode} />
                            </span>
                            <span
                              className={`truncate font-medium transition-colors duration-150 cursor-pointer ${
                                isDarkMode
                                  ? "text-gray-300 hover:text-white"
                                  : "text-gray-600 hover:text-gray-900"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (handleColumnEditStart) handleColumnEditStart(column);
                              }}
                            >
                              {column.name || column.id}
                            </span>
                          </>
                        )}
                        {sortConfig.key === column.id && (
                          <span className={`ml-1.5 text-[10px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {sortConfig.direction === "ascending" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>

                      {/* Action buttons - smooth reveal on hover */}
                      {column.id !== "image_previews" && column.type !== "image_group_toggle" && (
                        <div className="flex items-center ml-2 gap-0.5">
                          {/* Edit button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (handleColumnEditStart) handleColumnEditStart(column);
                            }}
                            className={`w-6 h-6 flex items-center justify-center rounded-md transition-all duration-150 ease-out
                              opacity-0 group-hover/header:opacity-100 translate-x-1 group-hover/header:translate-x-0 ${
                              isDarkMode ? "hover:bg-white/[0.08] text-gray-400" : "hover:bg-gray-200 text-gray-500"
                            }`}
                            title="Edit column"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          {/* Delete button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (handleColumnDelete) handleColumnDelete(column.id);
                            }}
                            className={`w-6 h-6 flex items-center justify-center rounded-md transition-all duration-150 ease-out
                              opacity-0 group-hover/header:opacity-100 translate-x-1 group-hover/header:translate-x-0 delay-[25ms] ${
                              deletingColumn === column.id
                                ? "opacity-50 cursor-not-allowed"
                                : isDarkMode
                                ? "hover:bg-red-500/20 text-gray-400 hover:text-red-400"
                                : "hover:bg-red-100 text-gray-400 hover:text-red-500"
                            }`}
                            disabled={deletingColumn === column.id}
                            title="Delete column"
                          >
                            {deletingColumn === column.id ? (
                              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      )}

                      {/* Edit Popover */}
                      {editingColumnId === column.id && (
                        <ColumnEditPopover
                          editColumnName={editColumnName}
                          setEditColumnName={setEditColumnName}
                          editColumnType={editColumnType}
                          setEditColumnType={setEditColumnType}
                          columnTypeOptions={columnTypeOptions || []}
                          onSave={handleColumnEditSave}
                          onCancel={() => setEditingColumnId && setEditingColumnId(null)}
                          isDarkMode={isDarkMode}
                        />
                      )}
                    </div>
                    {/* Column resize handle (the image group column sizes itself to its thumbnails) */}
                    {column.id !== "image_previews" && column.type !== "image_group_toggle" && (
                      <div
                        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize transition-colors ${
                          resizingColumn === column.id
                            ? (isDarkMode ? "bg-white/40" : "bg-gray-400")
                            : (isDarkMode ? "hover:bg-white/20" : "hover:bg-gray-300")
                        }`}
                        onMouseDown={(e) => handleResizeMouseDown(e, column.id)}
                      >
                        <div className={`absolute right-0 top-0 h-full w-3 -mr-1 ${resizingColumn === column.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}></div>
                      </div>
                    )}
                  </th>
                ))}
                {rightPadding > 0 && <th style={{ width: rightPadding, minWidth: rightPadding, padding: 0 }} />}
              </tr>
            </thead>
            <tbody
              className={`${isDarkMode ? "bg-[#111113]" : "bg-white"} transition-all duration-300 table-wrapper`}
              onMouseOver={handleCellMouseOver}
              onMouseOut={handleCellMouseOut}
            >
              {displayData.map((item, rowIndex) => (
                <tr
                  key={item.id}
                  data-row-id={item.id}
                  className={`transition-colors duration-100 table-row group/row ${
                    selectedRowId === item.id
                      ? `table-row-selected ${isDarkMode ? "bg-blue-500/10 hover:bg-blue-500/12" : "bg-blue-50 hover:bg-blue-100/70"}`
                      : automationActive && automationRunning && item.id > previousDataLength - newRowsCount && newRowsCount > 0
                      ? "animate-highlightRow"
                      : isDarkMode
                        ? "hover:bg-white/[0.02]"
                        : "hover:bg-gray-50/70"
                  }`}
                  style={hasImageColumns ? { height: 60 } : undefined}
                >
                  <td
                    className={`px-4 py-2.5 whitespace-nowrap text-xs tabular-nums border-r transition-colors duration-100 ${
                      isDarkMode ? "border-white/[0.04] text-gray-500" : "border-gray-100 text-gray-400"
                    }`}
                    style={{ width: "1%", minWidth: "62px" }}
                  >
                    {onRowDelete ? (
                      // Hovering the row swaps the index for a delete button in place —
                      // same reveal pattern as the column header actions.
                      <div className="relative flex items-center">
                        <span className="transition-opacity duration-150 group-hover/row:opacity-0">
                          {item.displayIndex}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onRowDelete && deletingRowId !== item.id) onRowDelete(item.id);
                          }}
                          disabled={deletingRowId === item.id}
                          className={`absolute inset-y-0 -left-1.5 w-6 flex items-center justify-center rounded-md transition-all duration-150 ease-out
                            opacity-0 group-hover/row:opacity-100 ${
                            deletingRowId === item.id
                              ? "opacity-50 cursor-not-allowed"
                              : isDarkMode
                              ? "hover:bg-red-500/20 text-gray-400 hover:text-red-400"
                              : "hover:bg-red-100 text-gray-400 hover:text-red-500"
                          }`}
                          title="Delete row"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      item.displayIndex
                    )}
                  </td>
                  {leftPadding > 0 && <td className={leftSpacerBorder} style={{ width: leftPadding, minWidth: leftPadding, padding: 0 }} />}
                  {visibleColumns.map((column) => (
                    <td
                      key={`${item.id}-${column.id}`}
                      className={`relative px-4 py-2.5 border-r transition-colors duration-100 ${
                        isDarkMode ? "border-white/[0.04]" : "border-gray-100"
                      } ${
                        editingCell?.rowId === item.id && editingCell?.columnId === column.id ? "p-0" : ""
                      }`}
                      style={{
                        width: (column.id === 'image_previews' || column.type === 'image_group_toggle') ? '1%' : undefined,
                        // Never cap the image group column: a max-width narrower than the
                        // thumbnails + "+N" button squeezes the column and the previews
                        // spill over the next column's text.
                        maxWidth: (column.id === 'image_previews' || column.type === 'image_group_toggle')
                          ? undefined
                          : (columnWidths[column.id] || 150),
                        minWidth: 80,
                        whiteSpace: (column.id === 'image_previews' || column.type === 'image_group_toggle') ? 'nowrap' : undefined,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (column.id !== "image_previews" && column.type !== "image" && column.type !== "image_group_toggle") {
                          handleCellEdit && handleCellEdit(item.id, column.id, item.data[column.id]);
                        }
                      }}
                    >
                      {editingCell?.rowId === item.id && editingCell?.columnId === column.id ? (
                        <div className="flex h-full">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className={`w-full h-full px-4 py-2.5 outline-none text-xs focus:ring-2 focus:ring-inset ${
                              isDarkMode ? "bg-[#0a0a0b] text-white focus:ring-blue-500/30" : "bg-white text-gray-900 focus:ring-blue-500/30"
                            }`}
                            autoFocus
                            onBlur={handleCellEditSave}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      ) : column.id === "image_previews" || column.type === "image_group_toggle" ? (
                        <div className="overflow-visible w-fit">
                          {renderDataCellValue(
                            item,
                            column.id,
                            column.type,
                            column,
                            processedData.displayColumns,
                            isDarkMode,
                            onOpenGallery
                              ? (images, initialIndex, galleryColumn) =>
                                  onOpenGallery(images, initialIndex, item, galleryColumn || column)
                              : null
                          )}
                        </div>
                      ) : (
                        <div
                          className="truncate"
                          data-tooltip={item.data[column.id] != null ? (typeof item.data[column.id] === "object" ? JSON.stringify(item.data[column.id], null, 2) : String(item.data[column.id])) : undefined}
                          data-tooltip-error={item.metadata?._error || undefined}
                        >
                          {renderDataCellValue(
                            item,
                            column.id,
                            column.type,
                            column,
                            processedData.displayColumns,
                            isDarkMode,
                            onOpenGallery
                              ? (images, initialIndex, galleryColumn) =>
                                  onOpenGallery(images, initialIndex, item, galleryColumn || column)
                              : null
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                  {rightPadding > 0 && <td style={{ width: rightPadding, minWidth: rightPadding, padding: 0 }} />}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Column position indicator for wide tables — bottom-left to avoid pagination overlap */}
        {scrollInfo.totalCols > 40 && (
          <div
            className={`fixed bottom-5 left-5 px-2.5 py-1 text-[10px] font-medium tabular-nums rounded-lg border ${
              isDarkMode
                ? "bg-[#1a1a1c]/90 border-white/[0.08] text-gray-400"
                : "bg-white/90 border-gray-200 text-gray-500"
            }`}
            style={{ backdropFilter: "blur(4px)", zIndex: Z_INDEX.FLOAT }}
          >
            Columns {scrollInfo.startCol}–{scrollInfo.endCol} of {scrollInfo.totalCols}
          </div>
        )}

        {/* Single delegated tooltip */}
        {tooltip && (
          <div
            className={`fixed max-w-xs px-2.5 py-1.5 text-[11px] rounded shadow-lg break-words pointer-events-none transition-opacity duration-150 ${
              isDarkMode
                ? "bg-zinc-800 text-gray-200 border border-zinc-700"
                : "bg-gray-800 text-white"
            }`}
            style={{
              top: tooltip.above ? undefined : tooltip.top + 4,
              bottom: tooltip.above ? `${window.innerHeight - tooltip.top + 4}px` : undefined,
              left: tooltip.left,
              zIndex: Z_INDEX.TOOLTIP,
            }}
          >
            {tooltip.value && <div className="whitespace-pre-wrap">{tooltip.value}</div>}
            {tooltip.error && (
              <div
                className={`mt-1 pt-1 border-t ${
                  isDarkMode
                    ? "border-zinc-600 text-red-400"
                    : "border-gray-600 text-red-300"
                }`}
              >
                {tooltip.error}
              </div>
            )}
          </div>
        )}

        {/* Delete feedback toasts */}
        {deletedColumnName && (
          <DeleteFeedback
            message={`Column "${deletedColumnName}" deleted`}
            isDarkMode={isDarkMode}
            onComplete={onDeleteFeedbackComplete}
          />
        )}
        {rowDeleteFeedback && (
          <DeleteFeedback
            message={rowDeleteFeedback}
            isDarkMode={isDarkMode}
            onComplete={onRowDeleteFeedbackComplete}
          />
        )}
      </div>
  );
});

export default TableComponent;
